import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, MessageSquare } from 'lucide-react';
import ApprovalCard from '../bots/ApprovalCard';
import BotAvatar from '../bots/BotAvatar';
import { ghostBtn } from '../bots/cards/CardShell';
import { useBotLabel } from '../bots/botDisplay';
import { useBotsActivityQuery, useMyBotsQuery } from '../../hooks/queries';
import { useTask } from '../../contexts/TaskContext';
import { cn } from '../../lib/utils';

const SHOWN = 4;

/**
 * "Needs you" on Home: what the bots wait on the person for, answered right here. Each
 * approval or question is its chat card (ApprovalCard: approve, decline, answer) with where
 * it came from and "Open chat"; work handed to a bot and done waits for review (the Bots
 * page's review row). Nothing waits: nothing shows.
 */
const HomeNeedsYou = () => {
  const { t } = useTranslation();
  const botLabel = useBotLabel();
  const { openTaskDetail } = useTask();
  const { data: mine } = useMyBotsQuery();
  const { data: activity } = useBotsActivityQuery();
  const byBot = useMemo(() => Object.fromEntries([...(mine?.bots || []), ...(mine?.queen ? [mine.queen] : [])].map((b) => [b.id, b])),
    [mine]);
  const navigate = useNavigate();
  const allApprovals = activity?.approvals || [];
  const reviews = activity?.reviews || [];
  const count = allApprovals.length + reviews.length;
  // Home keeps room for Today: the first few, the rest wait in Chats under "Needs you".
  const approvals = allApprovals.slice(0, SHOWN);
  if (!count) return null;

  return (
    <section className="space-y-2.5" data-testid="home-needs-you">
      <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <span className="w-2 h-2 rounded-full bg-attention" aria-hidden="true" />
        {t('bots.chat.group_needs_you')}
        <span className="font-normal text-muted-foreground tabular-nums">{count}</span>
        {allApprovals.length > SHOWN && (
          <Link to="/app/chats?needs_you=1" className="ml-auto text-xs font-normal text-muted-foreground hover:text-foreground">
            {t('bots.chat.all_chats')}
          </Link>
        )}
      </h2>
      {/* Two columns the cards flow down (a short card no longer leaves a hole beside a tall one). */}
      <div className="columns-1 md:columns-2 gap-3 [&>*]:mb-3 [&>*]:break-inside-avoid">
        {approvals.map((a) => {
          const bot = byBot[a.bot_id];
          const where = [botLabel(bot), a.project?.name].filter(Boolean).join(' · ');
          const open = a.chat_id ? `/app/chats/${a.chat_id}` : null;
          return (
            <div key={a.id} className="min-w-0 space-y-1 break-inside-avoid">
              <ApprovalCard approval={a} botName={botLabel(bot)} compact
                onOpenComputer={open ? () => navigate(open) : undefined} />
              <div className="flex items-center gap-2 px-1 min-w-0">
                <BotAvatar bot={bot} size="xs" />
                <span className="flex-1 min-w-0 truncate text-xs text-muted-foreground">{where}</span>
                {open ? (
                  <Link to={open} className={cn(ghostBtn, 'h-7 text-xs')} data-testid="home-needs-you-open">
                    <MessageSquare className="w-3.5 h-3.5" />{t('bots.chat.open_chat')}
                  </Link>
                ) : a.task_id && (
                  <button type="button" onClick={() => openTaskDetail(a.task_id)} className={cn(ghostBtn, 'h-7 text-xs')}>
                    {t('common.open')}
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {reviews.map((r) => (
          <button key={r.id} type="button" onClick={() => openTaskDetail(r.id)} data-testid="home-review"
            className="w-full text-left flex items-center gap-3 rounded-xl border border-sky-500/25 bg-sky-500/5 px-3 py-2.5 hover:border-sky-500/50">
            <BotAvatar bot={byBot[r.assigned_bot_id]} size={28} />
            <span className="flex-1 min-w-0">
              <span className="block truncate text-sm text-foreground">{r.title}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {[t('bots.home.card_review'), r.project?.name].filter(Boolean).join(' · ')}
              </span>
            </span>
            <Eye className="w-4 h-4 text-sky-500 shrink-0" />
          </button>
        ))}
      </div>
    </section>
  );
};

export default HomeNeedsYou;
